import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Briefcase, Home, CheckCircle, ExternalLink, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const T = {
  heroTitle: { de: 'Unsere Unternehmen', en: 'Our Companies' },
  heroSub: { de: 'Drei spezialisierte Tochtergesellschaften – vereint durch gemeinsame Werte und das Streben nach Exzellenz.', en: 'Three specialised subsidiaries – united by shared values and the pursuit of excellence.' },
  services: { de: 'Leistungen', en: 'Services' },
  targetClients: { de: 'Zielkunden', en: 'Target Clients' },
  learnMore: { de: 'Mehr erfahren', en: 'Learn more' },
  visitWebsite: { de: 'Zur Homepage', en: 'Visit Website' },
};

const subsidiaries = [
  {
    id: 'atob-logistic',
    name: 'AtoB Logistic Solutions GmbH',
    category: { de: 'Logistik & Transport', en: 'Logistics & Transport' },
    icon: Truck,
    logo: '/images/atob-logistic-logo-transparent.png',
    image: '/images/atob-truck-highway.jpg',
    website: 'https://www.atob-logistics.de',
    detailPath: '/unternehmen/atob-logistic',
    description: { de: 'AtoB Logistic Solutions GmbH ist Ihr zuverlässiger Partner für professionelle Logistiklösungen. Wir bieten maßgeschneiderte Transportdienstleistungen für Unternehmen jeder Größe.', en: 'AtoB Logistic Solutions GmbH is your reliable partner for professional logistics solutions. We offer tailored transport services for businesses of all sizes.' },
    management: { de: 'Geschäftsführer: Admir Basic (Alleiniger Gesellschafter)', en: 'Managing Director: Admir Basic (Sole Shareholder)' },
    services: {
      de: ['Fernverkehr mit 40t-Sattelzügen für nationale und internationale Transporte', 'Nahverkehr mit 3,5t-Fahrzeugen inkl. Ladebordwand', 'Komplette Logistiklösungen von A bis B', 'Zuverlässige Termintreue und transparente Kommunikation', 'Moderne Fahrzeugflotte mit GPS-Tracking'],
      en: ['Long-distance transport with 40t semi-trailers for national and international shipments', 'Local delivery with 3.5t vehicles incl. tail lift', 'Complete logistics solutions from A to B', 'Reliable punctuality and transparent communication', 'Modern vehicle fleet with GPS tracking'],
    },
    targetClients: { de: 'B2B-Unternehmen mit Bedarf an zuverlässigen Frachtdienstleistungen', en: 'B2B companies requiring reliable freight services' }
  },
  {
    id: 'dsb-business',
    name: 'DSB Business Solutions GmbH',
    category: { de: 'Unternehmensberatung', en: 'Business Consulting' },
    icon: Briefcase,
    logo: '/images/edited-dsb-logo.png',
    image: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=800',
    website: 'https://www.dsb-deutschland.org',
    detailPath: '/unternehmen/dsb-business',
    description: { de: 'DSB Business Solutions GmbH bietet umfassende Beratungsdienstleistungen für den deutschen Mittelstand. Unser Expertenteam unterstützt Sie in allen Bereichen der Unternehmensführung.', en: 'DSB Business Solutions GmbH offers comprehensive consulting services for German SMEs. Our expert team supports you in all areas of corporate management.' },
    management: { de: 'Geschäftsführende Gesellschafter: Admir Basic, Alessandro Costantino', en: 'Managing Partners: Admir Basic, Alessandro Costantino' },
    services: {
      de: ['Datenschutz & DSGVO-Compliance – vollumfängliche Beratung und Umsetzung', 'IT-Sicherheit & Awareness-Schulungen für Ihre Mitarbeiter', 'HR-Beratung und Personalentwicklung', 'IT-Consulting & digitale Transformation', 'Organisationsentwicklung und Prozessoptimierung'],
      en: ['Data Protection & GDPR Compliance – comprehensive consulting and implementation', 'IT Security & Awareness Training for your employees', 'HR Consulting and Personnel Development', 'IT Consulting & Digital Transformation', 'Organisational Development and Process Optimisation'],
    },
    targetClients: { de: 'Kleine und mittelständische Unternehmen, die professionelle Beratung suchen', en: 'Small and medium-sized enterprises seeking professional consulting' }
  },
  {
    id: 'atob-objektservice',
    name: 'AtoB Objektservice GmbH',
    category: { de: 'Entrümpelung & Entsorgung', en: 'Clearance & Disposal' },
    icon: Home,
    logo: '/images/atob-objektservice-logo-dark-bg.png',
    image: 'https://static.prod-images.emergentagent.com/jobs/73c84d3f-4e42-4579-a2eb-11d730a48d79/images/7db6b490dd16a6228f96897724d9a6cd531467de95afb5e10860e5900f649236.png',
    website: 'https://www.atob-objektservice.de',
    detailPath: '/unternehmen/atob-objektservice',
    description: { de: 'AtoB Objektservice GmbH steht für professionelle und umweltbewusste Entrümpelung und Entsorgung. Wir übernehmen alle Arbeiten schnell, sauber und zuverlässig.', en: 'AtoB Objektservice GmbH stands for professional and eco-friendly clearance and disposal. We handle all work quickly, cleanly and reliably.' },
    management: { de: 'Geschäftsführer: Admir Basic (Gesellschafter), Alessandro Costantino', en: 'Managing Directors: Admir Basic (Shareholder), Alessandro Costantino' },
    services: {
      de: ['Haushaltsauflösungen – komplett und besenrein', 'Gewerbliche und private Entrümpelung', 'Fachgerechte Entsorgung und Recycling', 'Reinigungsservice nach Räumung', 'Transport- und Umzugsservice'],
      en: ['Household clearances – complete and broom-clean', 'Commercial and private decluttering', 'Professional disposal and recycling', 'Cleaning service after clearance', 'Transport and moving service'],
    },
    targetClients: { de: 'Privatpersonen, Immobilienunternehmen und Hausverwaltungen', en: 'Private individuals, real estate companies and property managers' }
  }
];

export default function Unternehmen() {
  const { language } = useLanguage();

  return (
    <main data-testid="unternehmen-page" className="pt-20">
      <section className="bg-[#0F1A2E] py-24 md:py-32">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="accent-line mb-6"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">{T.heroTitle[language]}</h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      {subsidiaries.map((subsidiary, index) => (
        <SubsidiarySection key={subsidiary.id} subsidiary={subsidiary} index={index} isReversed={index % 2 === 1} language={language} />
      ))}
    </main>
  );
}

function SubsidiarySection({ subsidiary, index, isReversed, language }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} data-testid={`subsidiary-section-${subsidiary.id}`} className={`section-spacing ${index % 2 === 0 ? 'bg-[#0A1220]' : 'bg-[#0F1A2E]'}`}>
      <div className="container-main">
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
          <motion.div initial={{ opacity: 0, x: isReversed ? 30 : -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }} className={`relative ${isReversed ? 'lg:order-2' : ''}`}>
            <div className="relative overflow-hidden">
              <img src={subsidiary.image} alt={subsidiary.name} className="w-full h-80 md:h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2E]/30 to-transparent"></div>
            </div>
            <div className="absolute -bottom-4 left-6 bg-[#F5A623] text-white px-6 py-3">
              <span className="text-sm font-semibold uppercase tracking-wider">{subsidiary.category[language]}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: isReversed ? -30 : 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} className={isReversed ? 'lg:order-1' : ''}>
            <div className="mb-10"><img src={subsidiary.logo} alt={subsidiary.name} className="h-28 md:h-36 w-auto object-contain" /></div>
            <p className="text-lg text-white/70 leading-relaxed mb-6">{subsidiary.description[language]}</p>
            <p className="text-sm text-[#F5A623] font-medium mb-8">{subsidiary.management[language]}</p>
            <div className="space-y-4 mb-8">
              <h3 className="font-semibold text-white uppercase tracking-wider text-sm">{T.services[language]}</h3>
              <ul className="space-y-3">
                {subsidiary.services[language].map((service, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: 20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }} className="flex items-start space-x-3">
                    <CheckCircle size={18} className="text-[#F5A623] flex-shrink-0 mt-1" />
                    <span className="text-white/70">{service}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="p-6 bg-[#152238] border-l-4 border-[#F5A623] rounded-xl mb-6">
              <p className="text-sm text-white/50 uppercase tracking-wider mb-2">{T.targetClients[language]}</p>
              <p className="text-white">{subsidiary.targetClients[language]}</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to={subsidiary.detailPath} data-testid={`detail-link-${subsidiary.id}`} className="inline-flex items-center gap-2 bg-[#0F1A2E] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#F5A623] transition-colors">
                <ChevronRight size={16} />{T.learnMore[language]}
              </Link>
              <a href={subsidiary.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#F5A623] text-[#F5A623] px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#F5A623] hover:text-[#0F1A2E] transition-colors">
                <ExternalLink size={16} />{T.visitWebsite[language]}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
